import { useQuery } from '@tanstack/react-query'
import {
  getPartnerOffer,
  getPartnerProperty,
  getPartnerQuestions,
  getPartnerSession,
  PARTNER_CAPABILITIES,
  redeemPartnerLink,
  type PartnerOffer,
  type PartnerProperty,
  type PartnerQuestion,
  type PartnerSummary,
} from '../api/partnerLinks'

/** Everything the page renders from, loaded as one unit. */
export interface PartnerLinkView {
  partner: PartnerSummary
  property: PartnerProperty
  /** What the link may do; the page renders only the cards it can act on. */
  capabilities: readonly string[]
  /** The link subject's newest offer; null when there is none or the link cannot see it. */
  offer: PartnerOffer | null
  /** Newest first; empty when there are none or the link cannot read them. */
  questions: PartnerQuestion[]
}

// Keyed by the URL path, never by the token: the token is a long-lived
// credential and stays out of the query cache.
export const partnerLinkKeys = {
  all: ['partner-link'] as const,
  view: (slug: string, propertyId: string) => [...partnerLinkKeys.all, slug, propertyId] as const,
}

/** The agent can act while the page sits open, so the view goes stale after a minute. */
const STALE_TIME_MS = 60_000

/**
 * Open the session, then load the property it is bound to, the subject's own
 * offer and its questions.
 *
 * With a token, the link is redeemed (safe to repeat). Without one — the page
 * was reloaded after the token left the address bar — the session cookie is
 * read instead, which works for as long as the cookie lives. The property
 * loaded is the one the session names, not the one in the URL path. The offer
 * and questions reads run side by side, each only when the link carries the
 * capability for it.
 */
export function usePartnerLink(token: string, slug: string, propertyId: string) {
  return useQuery({
    queryKey: partnerLinkKeys.view(slug, propertyId),
    queryFn: async (): Promise<PartnerLinkView> => {
      const session = token ? await redeemPartnerLink(token) : await getPartnerSession()
      const property = await getPartnerProperty(session.property_id)
      const carries = (capability: string) => session.capabilities.includes(capability)
      const [offer, questions] = await Promise.all([
        carries(PARTNER_CAPABILITIES.offerViewOwn) ? getPartnerOffer(property.id) : Promise.resolve(null),
        carries(PARTNER_CAPABILITIES.propertyRequestInfo)
          ? getPartnerQuestions(property.id)
          : Promise.resolve<PartnerQuestion[]>([]),
      ])
      return {
        partner: session.partner,
        property,
        capabilities: session.capabilities,
        offer,
        questions,
      }
    },
    retry: false,
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })
}
